/**
 * The crate: an ordered list of records plus where the needle is in it.
 *
 * Shuffle and repeat live here rather than on the deck, so "what plays next"
 * is decidable without touching an audio engine at all.
 */

import { clamp } from '../lib/math.ts';
import type { RepeatMode, Track } from '../types/media.ts';

/** Returned by {@link Queue.next} when there is nothing more to play. */
export const NO_TRACK = -1;

export class Queue {
  tracks: Track[] = [];
  index = -1;
  shuffle = false;
  repeat: RepeatMode = 'off';

  get length(): number {
    return this.tracks.length;
  }

  get current(): Track | null {
    return this.at(this.index);
  }

  /** Replaces the crate. The list is copied, so later caller mutation is ignored. */
  set(tracks: readonly Track[], startIndex = 0): void {
    this.tracks = tracks.slice();
    this.index = clamp(startIndex, 0, Math.max(0, this.tracks.length - 1));
  }

  /** The track at an index, or null when out of bounds. */
  at(i: number): Track | null {
    return this.tracks[i] ?? null;
  }

  /**
   * The index `step` records away, honouring shuffle and repeat.
   * {@link NO_TRACK} means the end of the crate.
   */
  next(step = 1): number {
    if (!this.tracks.length) return NO_TRACK;
    if (this.repeat === 'one') return this.index;
    if (this.shuffle) return this.#randomOther();

    const n = this.index + step;
    if (n >= this.tracks.length) return this.repeat === 'all' ? 0 : NO_TRACK;
    if (n < 0) return this.repeat === 'all' ? this.tracks.length - 1 : NO_TRACK;
    return n;
  }

  #randomOther(): number {
    if (this.tracks.length === 1) return 0;
    let n = this.index;
    while (n === this.index) n = Math.floor(Math.random() * this.tracks.length);
    return n;
  }
}
